import { useState } from "react";
import { Menu, X, Globe, Heart, LogOut } from "lucide-react";
import { useAccount } from "@/lib/account";
import { GoogleSignIn } from "@/components/GoogleSignIn";
import { LOCALES, LOCALE_NAMES, ORDERING_ENABLED, SOCIAL_LINKS, type SocialId } from "@artlove365/shared";
import { Link, useRouter } from "@/lib/router";
import { useI18n } from "@/lib/i18n";

export function Layout({ children }: { children: React.ReactNode }) {
  const { t, href, locale } = useI18n();
  const { loc, navigate } = useRouter();
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { favorites, user, googleClientId, signOut } = useAccount();
  const nav = [
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/artists", label: t("nav.artists") },
    { to: "/how-it-works", label: t("nav.how") },
    { to: "/custom", label: t("nav.custom") },
    // Ordering is hidden for now (ORDERING_ENABLED): no "Track order" link.
    ...(ORDERING_ENABLED ? [{ to: "/orders", label: t("nav.track") }] : []),
  ];
  // Switching language keeps the visitor on the same page.
  const switchTo = (l: string) => {
    const rest = loc.pathname.split("/").slice(2).join("/");
    navigate(`/${l}${rest ? "/" + rest : ""}${loc.search.size ? "?" + loc.search : ""}`);
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-b border-line bg-canvas/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4">
          <Link href={href()} className="font-serif text-2xl font-bold tracking-tight">
            ArtLove<span className="text-accent">365</span>
          </Link>
          <nav className="ml-auto hidden items-center gap-6 text-sm md:flex">
            {nav.map((n) => (
              <Link key={n.to} href={href(n.to)} className={loc.pathname.startsWith(href(n.to)) ? "text-accent" : "hover:text-accent"}>
                {n.label}
              </Link>
            ))}
          </nav>
          <Link href={href("/favorites")} aria-label={t("fav.nav")} title={t("fav.nav")} className="relative ml-auto rounded-full p-2 hover:bg-line/50 md:ml-0">
            <Heart className={favorites.size ? "size-5 fill-accent text-accent" : "size-5"} />
            {favorites.size > 0 && <span className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-4 text-accent-ink">{favorites.size > 99 ? "99+" : favorites.size}</span>}
          </Link>
          {(user || googleClientId) && (
            <div className="relative">
              <button onClick={() => setAccountOpen((o) => !o)} aria-expanded={accountOpen} className="flex items-center rounded-full text-sm hover:opacity-80">
                {user?.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="size-8 rounded-full" />
                  : user ? <span className="grid size-8 place-items-center rounded-full bg-accent text-accent-ink">{(user.name ?? user.email)[0].toUpperCase()}</span>
                  : <span className="rounded-full border border-line px-3 py-1.5">{t("account.signIn")}</span>}
              </button>
              {accountOpen && (
                <div className="absolute right-0 top-11 z-40 w-72 rounded-xl border border-line bg-surface p-4 shadow-xl">
                  {user ? (
                    <>
                      <p className="text-xs text-muted">{t("account.signedInAs")}</p>
                      <p className="truncate font-medium">{user.name ?? user.email}</p>
                      <p className="truncate text-sm text-muted">{user.email}</p>
                      <Link href={href("/favorites")} onClick={() => setAccountOpen(false)} className="mt-3 flex items-center gap-2 rounded-md py-2 hover:text-accent"><Heart className="size-4" />{t("fav.title")} ({favorites.size})</Link>
                      <button onClick={() => { signOut(); setAccountOpen(false); }} className="flex items-center gap-2 py-2 text-sm text-muted hover:text-accent"><LogOut className="size-4" />{t("account.signOut")}</button>
                    </>
                  ) : (
                    <>
                      <p className="font-serif text-lg font-semibold">{t("account.signInTitle")}</p>
                      <p className="mb-3 mt-1 text-sm text-muted">{t("fav.syncHint")}</p>
                      <GoogleSignIn onDone={() => setAccountOpen(false)} />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <label className="flex items-center gap-1 text-sm">
            <Globe className="size-4 text-muted" aria-hidden />
            <span className="sr-only">Language</span>
            <select value={locale} onChange={(e) => switchTo(e.target.value)} className="bg-transparent py-1 outline-none">
              {LOCALES.map((l) => <option key={l} value={l}>{LOCALE_NAMES[l]}</option>)}
            </select>
          </label>
          <button className="md:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu" aria-expanded={open}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <nav className="flex flex-col gap-1 border-t border-line px-4 py-3 md:hidden">
            {nav.map((n) => (
              <Link key={n.to} href={href(n.to)} onClick={() => setOpen(false)} className="py-2">{n.label}</Link>
            ))}
          </nav>
        )}
      </header>
      {/* min height keeps the footer below the fold while page data arrives (no layout shift) */}
      <main className="min-h-[100svh] flex-1">{children}</main>
      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-8 text-sm text-muted sm:flex-row sm:justify-between">
          <p>© {new Date().getFullYear()} ArtLove365 · {t("footer.tagline")}</p>
          <nav className="flex flex-wrap gap-x-4 gap-y-1">
            {(["movements", "museums", "subjects", "genres"] as const).map((k) => (
              <Link key={k} href={href(`/${k}`)} className="hover:text-accent">{t(`browse.${k}`)}</Link>
            ))}
          </nav>
          <nav aria-label={t("footer.follow")} className="flex items-center gap-3">
            <span>{t("footer.follow")}:</span>
            {SOCIAL_LINKS.map((s) => (
              <a key={s.id} href={s.url} target="_blank" rel="noopener me" aria-label={s.label} title={s.label} className="hover:text-accent">
                <svg viewBox="0 0 24 24" className="size-5 fill-current" aria-hidden="true"><path d={SOCIAL_ICON[s.id]} /></svg>
              </a>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}

// Simple brand marks (24x24 paths) for the footer; lucide no longer ships brand icons.
const SOCIAL_ICON: Record<SocialId, string> = {
  facebook: "M13.5 22v-8.2h2.8l.4-3.2h-3.2V8.5c0-.9.3-1.6 1.6-1.6h1.7V4.1c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.4H7.3v3.2h2.8V22h3.4Z",
  linkedin: "M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.5h4V21H3V9.5Zm6.5 0h3.8v1.6h.1c.5-1 1.8-2 3.8-2 4 0 4.8 2.6 4.8 6V21h-4v-5.2c0-1.2 0-2.8-1.7-2.8s-2 1.3-2 2.7V21h-4V9.5Z",
  instagram: "M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm0 8.2a3.2 3.2 0 1 1 0-6.4 3.2 3.2 0 0 1 0 6.4ZM17.3 5.5a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4ZM12 2c-2.7 0-3 0-4.1.1C4.3 2.3 2.3 4.3 2.1 7.9 2 9 2 9.3 2 12s0 3 .1 4.1c.2 3.6 2.2 5.6 5.8 5.8 1.1.1 1.4.1 4.1.1s3 0 4.1-.1c3.6-.2 5.6-2.2 5.8-5.8.1-1.1.1-1.4.1-4.1s0-3-.1-4.1c-.2-3.6-2.2-5.6-5.8-5.8C15 2 14.7 2 12 2Z",
  youtube: "M23 7.2a3 3 0 0 0-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 0 0 1 7.2 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.8a3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .5-4.8 31 31 0 0 0-.5-4.8ZM9.8 15V9l5.8 3-5.8 3Z",
  x: "M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.2-8.3L2 3h6.4l4.4 5.8L17.8 3Zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5Z",
  pinterest: "M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.4 1.8-2.4.9 0 1.3.6 1.3 1.4 0 .9-.5 2.1-.8 3.3-.2 1 .5 1.8 1.5 1.8 1.8 0 3.1-1.9 3.1-4.6 0-2.4-1.7-4.1-4.2-4.1-2.9 0-4.5 2.1-4.5 4.4 0 .9.3 1.8.8 2.3.1.1.1.2.1.3l-.3 1.2c0 .2-.2.2-.4.1-1.3-.6-2.1-2.5-2.1-4 0-3.3 2.4-6.3 6.9-6.3 3.6 0 6.4 2.6 6.4 6 0 3.6-2.3 6.5-5.4 6.5-1.1 0-2.1-.6-2.4-1.2l-.7 2.5c-.2.9-.9 2.1-1.3 2.8A10 10 0 1 0 12 2Z",
  tiktok: "M16.6 2h-3.3v13.4a2.9 2.9 0 1 1-2-2.8V9.2a6.3 6.3 0 1 0 5.3 6.2V8.6a7.6 7.6 0 0 0 4.4 1.4V6.7a4.4 4.4 0 0 1-4.4-4.7Z",
};
